import {
  Center,
  SimpleGrid,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import SelfIpoApplicationCard from 'presentation/components/modules/selfIpo/SelfIpoApplicationCard';
import { canOpenOfferingFromApplication } from 'presentation/components/modules/selfIpo/selfIpoUtils';
import AppCard from 'presentation/components/shared/card/AppCard';
import ErrorContainer from 'presentation/components/shared/ErrorContainer';
import GridHeading from 'presentation/components/shared/grid/GridHeading';
import ButtonTabs from 'presentation/components/shared/tabs/ButtonTabs';
import CancelSelfIpoApplicationModal from 'presentation/containers/modules/selfIpo/CancelSelfIpoApplicationModal';
import SettingsLayout from 'presentation/layout/modules/settings/SettingsLayout';
import { FC, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AppRoute from 'router/AppRoute';
import { useFetchSelfIpoApplicationsQuery } from 'rtkQuery/sharespro/queries/selfIpo';

const SELF_IPO_ACTIVE_APPLICATION_STATUSES = new Set([1]);
const SELF_IPO_CANCELLED_APPLICATION_STATUSES = new Set([3, 5]);

const SelfIpoApplicationsPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedApplication, setSelectedApplication] =
    useState<SelfIpoApplication | null>(null);
  const { data, error, isFetching, refetch } =
    useFetchSelfIpoApplicationsQuery();

  let content;
  let wrapByCard = true;
  if (data) {
    const filteredApplications = data.filter((application) => {
      const statuses =
        tabIndex === 0
          ? SELF_IPO_ACTIVE_APPLICATION_STATUSES
          : SELF_IPO_CANCELLED_APPLICATION_STATUSES;

      return statuses.has(application.status);
    });

    if (filteredApplications.length > 0) {
      wrapByCard = false;
      content = (
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="20px">
          {filteredApplications.map((application) => {
            const canOpen = canOpenOfferingFromApplication(application);

            return (
              <SelfIpoApplicationCard
                key={application.id}
                application={application}
                onClick={
                  canOpen
                    ? () =>
                        navigate(AppRoute.selfIpoDetail(application.offeringId), {
                          state: { openedFromApplications: true },
                        })
                    : undefined
                }
                onCancel={() => {
                  setSelectedApplication(application);
                  onOpen();
                }}
              />
            );
          })}
        </SimpleGrid>
      );
    } else {
      content = <Text>{t('listIsEmpty')}</Text>;
    }
  } else if (isFetching) {
    content = <Spinner />;
  } else if (error) {
    content = (
      <ErrorContainer
        error={error}
        onRetry={() => {
          refetch();
        }}
      />
    );
  } else {
    content = <Spinner />;
  }

  if (wrapByCard) {
    content = (
      <AppCard p="20px">
        <Center>{content}</Center>
      </AppCard>
    );
  }

  return (
    <SettingsLayout>
      <Helmet>
        <title>{t('selfIpoApplicationsTitle')}</title>
      </Helmet>
      <GridHeading>{t('selfIpoApplicationsTitle')}</GridHeading>
      <ButtonTabs
        mb="20px"
        onChange={setTabIndex}
        index={tabIndex}
        items={[
          t('selfIpoApplicationsActiveTab'),
          t('selfIpoApplicationsCancelledTab'),
        ]}
      />
      {content}
      {selectedApplication && (
        <CancelSelfIpoApplicationModal
          applicationId={selectedApplication.id}
          isOpen={isOpen}
          onClose={onClose}
          onSuccess={() => {
            refetch();
          }}
        />
      )}
    </SettingsLayout>
  );
};

export default SelfIpoApplicationsPage;
